import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MetaHead from "./MetaHead";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  fetchJournalEntryByDate,
  deleteJournalEntryById,
} from "../store/slices/journalSlice";
import { JournalEntry } from "../types";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import ConfirmationModal from "./ConfirmationModal";
import AppSidebar from "./AppSidebar";

const JournalDetails: React.FC = () => {
  const { t } = useTranslation();
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const journalEntries = useAppSelector((state) => state.journal.items);

  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    trackPageVisit("journal_details");

    const loadJournalEntry = async () => {
      if (!date) return;

      try {
        // Find the journal entry for this date from Redux store
        const entryForDate = journalEntries.find((e) => e.date === date);

        if (entryForDate) {
          setEntry(entryForDate);
        } else {
          // If not in store, try to fetch it by date
          const result = await dispatch(fetchJournalEntryByDate(date)).unwrap();
          if (result) {
            setEntry(result);
          } else {
            setError("Journal entry not found for this date");
          }
        }
      } catch (err) {
        console.error("Error loading journal entry:", err);
        setError("Failed to load journal entry");
      } finally {
        setLoading(false);
      }
    };

    loadJournalEntry();
  }, [date, dispatch, journalEntries]);

  const handleBack = () => {
    navigate(`/day/${date}`);
  };

  const handleEdit = () => {
    if (entry) {
      navigate(`/edit-journal-entry/${entry.date}`);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (entry) {
      try {
        trackUserInteraction(
          "delete_journal_entry",
          "journal_management",
          entry.id,
        );
        await dispatch(deleteJournalEntryById(entry.id));
        navigate("/app");
      } catch (error) {
        console.error("Error deleting journal entry:", error);
        // TODO: Show error message
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  const getPreviousDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() - 1);
    return date.toISOString().split("T")[0];
  };

  const getNextDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + 1);
    return date.toISOString().split("T")[0];
  };

  const handlePrevious = () => {
    if (date) {
      const prevDate = getPreviousDate(date);
      navigate(`/journal-details/${prevDate}`);
    }
  };

  const handleNext = () => {
    if (date) {
      const nextDate = getNextDate(date);
      const today = new Date().toISOString().split("T")[0];
      if (nextDate <= today) {
        navigate(`/journal-details/${nextDate}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-14 h-14 bg-[#e87b3f]/20 border border-[#e87b3f]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl animate-spin">?</span>
          </div>
          <h2 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white mb-1">
            {t("journalDetails.loading")}
          </h2>
        </div>
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-14 h-14 bg-[#e87b3f]/20 border border-[#e87b3f]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">??</span>
          </div>
          <h2 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white mb-2">
            {t("journalDetails.notFound")}
          </h2>
          <p className="text-[17px] text-gray-500 dark:text-white/40 mb-6">
            {error ||
              t("journalDetails.notFoundMessage", { date: formatDate(date!) })}
          </p>
          <button
            onClick={handleBack}
            className="px-5 py-2.5 bg-[#e87b3f] text-white text-[18px] font-semibold rounded-xl hover:opacity-90 transition-all"
          >
            {t("journalDetails.backToCalendar")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={`${entry!.title} - Journal Entry - Streak Spark`}
        description={`View journal entry: ${entry!.title} in Streak Spark.`}
        keywords="journal, entry, details, view, edit, delete, mood"
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        <div className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* TOPBAR */}
          <header className="flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleBack}
              aria-label={t("journalDetails.back") as string}
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#e87b3f]/20 border border-[#e87b3f]/30 flex items-center justify-center transition-all hover:bg-[#e87b3f]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#e87b3f"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("journalDetails.pageTitle")}
            </p>
            <button
              onClick={handleEdit}
              aria-label="Edit entry"
              className="tap-scale rounded-[9px] bg-[#e87b3f]/20 border border-[#e87b3f]/30 flex items-center justify-center text-[#e87b3f] transition-all hover:bg-[#e87b3f]/30 text-sm p-2"
            >
              Edit
            </button>
          </header>

          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-3xl mx-auto pb-8 space-y-4">
              <div className="rounded-2xl border border-[#e87b3f]/20 dark:border-[#e87b3f]/30 bg-gradient-to-br from-[#e87b3f]/[0.07] to-transparent dark:from-[#e87b3f]/[0.09] p-4 lg:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#e87b3f]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[24px] leading-none">📔</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[19px] font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                        {entry!.title}
                      </p>
                      <p className="text-[16px] text-gray-600 dark:text-white/45 leading-relaxed">
                        {formatDate(entry!.date)}
                      </p>
                    </div>
                  </div>
                  <span className="text-[15px] font-semibold px-2.5 py-1 rounded-full shrink-0 bg-[#e87b3f]/20 border border-[#e87b3f]/30 text-[#e87b3f]">
                    {t(`journalDetails.moods.${entry!.mood}`) || entry!.mood}
                  </span>
                </div>
              </div>

              {/* Prev/Next navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={handlePrevious}
                  aria-label={t("journalDetails.previousDay") as string}
                  className="tap-scale flex items-center gap-1.5 px-3 py-2 bg-[#e87b3f]/15 border border-[#e87b3f]/25 text-[#e87b3f] text-[17px] font-medium rounded-xl hover:bg-[#e87b3f]/25 transition-all"
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M10 3L5 8l5 5"
                      stroke="#e87b3f"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t("journalDetails.previousDay")}
                </button>
                <span className="text-[17px] text-gray-500 dark:text-white/40 font-medium">
                  {formatDate(entry!.date)}
                </span>
                <button
                  onClick={handleNext}
                  disabled={
                    date
                      ? getNextDate(date) >
                        new Date().toISOString().split("T")[0]
                      : false
                  }
                  aria-label={t("journalDetails.nextDay") as string}
                  className={`tap-scale flex items-center gap-1.5 px-3 py-2 bg-[#e87b3f]/15 border border-[#e87b3f]/25 text-[#e87b3f] text-[17px] font-medium rounded-xl hover:bg-[#e87b3f]/25 transition-all ${date && getNextDate(date) > new Date().toISOString().split("T")[0] ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  {t("journalDetails.nextDay")}
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M6 3l5 5-5 5"
                      stroke="#e87b3f"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Entry card */}
              <div className="space-y-5">
                {/* Title */}
                <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                  <p className="text-[13px] font-medium uppercase tracking-widest text-gray-500 dark:text-white/30 mb-1">
                    {t("journalDetails.title")}
                  </p>
                  <h2 className="text-[24px] font-semibold tracking-tight text-gray-900 dark:text-white">
                    {entry!.title}
                  </h2>
                </div>

                {/* Content */}
                <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                  <p className="text-[13px] font-medium uppercase tracking-widest text-gray-500 dark:text-white/30 mb-1">
                    {t("journalDetails.content")}
                  </p>
                  <p className="text-[18px] text-gray-900 dark:text-white/80 leading-relaxed whitespace-pre-wrap">
                    {entry!.content}
                  </p>
                </div>

                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-black/[0.06] dark:border-white/[0.07]">
                  <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-3">
                    <p className="text-[15px] text-gray-500 dark:text-white/30 mb-0.5">
                      {t("journalDetails.mood")}
                    </p>
                    <p className="text-[17px] font-medium text-gray-900 dark:text-white">
                      {t(`journalDetails.moods.${entry!.mood}`) || entry!.mood}
                    </p>
                  </div>
                  <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-3">
                    <p className="text-[15px] text-gray-500 dark:text-white/30 mb-0.5">
                      {t("journalDetails.date")}
                    </p>
                    <p className="text-[17px] font-medium text-gray-900 dark:text-white">
                      {formatDate(entry!.date)}
                    </p>
                  </div>
                </div>

                {/* Tags */}
                {entry!.tags && entry!.tags.length > 0 && (
                  <div>
                    <p className="text-[13px] font-medium uppercase tracking-widest text-gray-500 dark:text-white/30 mb-2">
                      {t("journalDetails.tags")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {entry!.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2.5 py-1 text-[15px] font-medium bg-[#e87b3f]/15 border border-[#e87b3f]/25 text-[#e87b3f] rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3 pt-4 border-t border-black/[0.07] dark:border-white/[0.08]">
                  <button
                    onClick={handleEdit}
                    className="flex-1 py-3 bg-[#e87b3f]/15 border border-[#e87b3f]/25 text-[#e87b3f] text-[18px] font-medium rounded-xl hover:bg-[#e87b3f]/25 transition-all"
                  >
                    {t("journalDetails.edit")}
                  </button>
                  <button
                    onClick={handleDelete}
                    className="py-3 px-4 bg-red-500/10 border border-red-500/20 text-red-500 text-[18px] font-medium rounded-xl hover:bg-red-500/20 transition-all"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Journal Entry"
        message="Are you sure you want to delete this journal entry? This action cannot be undone."
      />
    </>
  );
};

export default JournalDetails;
